import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  Autocomplete,
  Chip,
  Alert,
} from '@mui/material';
import {
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  Inventory2 as Inventory2Icon,
  Search as SearchIcon,
  Description as DescriptionIcon,
  FactCheck as FactCheckIcon,
  HourglassEmpty as HourglassEmptyIcon,
  LockOutlined as LockOutlinedIcon,
} from '@mui/icons-material';
import { tienePermisosModulo, puedeEditarProgramaProductos, puedeAccionProductos } from 'lib/seguridad/permisosPorModulo';
import { FP, PageHeader, StatCard, fpInputSx } from 'components/financepro';
import { useProductosMaterialContext } from 'context';

/** Misma fila `programa` que «Entradas / salidas inventario» — operaciones de inventario físico. */
const PROGRAMA_CONTEO_FISICO = 'conteofisicoinventario';
const statusChip = (row, localidadSeleccionada) => {
  const codigo = (row?.codigo || '').trim();
  const lidFila = row?.localidad_id != null && row?.localidad_id !== '' ? String(row.localidad_id).trim() : '';
  const lidSel = String(localidadSeleccionada || '')
    .split('-')[0]
    .trim();
  const contado = !!(codigo && lidFila && lidSel && lidFila === lidSel);
  if (contado) {
    return (
      <Chip
        size="small"
        label="Contado"
        sx={{ bgcolor: FP.color.successSoft, color: FP.color.successDark, fontWeight: 600 }}
      />
    );
  }
  return (
    <Chip
      size="small"
      label="Pendiente"
      sx={{ bgcolor: FP.color.surfaceAlt, color: FP.color.textSecondary, fontWeight: 600 }}
    />
  );
};

const filaContada = (row, localidadSeleccionada) => {
  const codigo = (row?.codigo || '').trim();
  const lidFila = row?.localidad_id != null && row?.localidad_id !== '' ? String(row.localidad_id).trim() : '';
  const lidSel = String(localidadSeleccionada || '')
    .split('-')[0]
    .trim();
  return !!(codigo && lidFila && lidSel && lidFila === lidSel);
};

/**
 * Conteo físico por localidad: consulta inventario filtrado por local,
 * registra cantidad física en tabla `conteo_fisico_inventario` y marca CONTADO cuando hay código y localidad.
 * Al cerrar, se bloquean nuevos conteos y se ajusta `inicial` en la tabla de inventario para igualar la existencia a la cantidad física.
 */
const ConteoFisicoInventario = () => {
  const { controller, selectedCompany, showSuccess, showError } = useProductosMaterialContext();
  const { nombreNegocio, usuarioLog, claveLog, roleDetalles } = controller;

  const servidor = process.env.REACT_APP_SERVIDOR || '';

  const puedeLeer = tienePermisosModulo(roleDetalles, PROGRAMA_CONTEO_FISICO);
  const puedeModificar = puedeEditarProgramaProductos(roleDetalles, PROGRAMA_CONTEO_FISICO);
  // Cerrar el conteo es irreversible: exige el flag modificar (misma
  // convención que el módulo de conteos de inventario serializado).
  const puedeCerrar = puedeAccionProductos(roleDetalles, PROGRAMA_CONTEO_FISICO, 'modificar');

  const tablaInventario = useMemo(() => {
    return "productosnoserializados";
  }, [selectedCompany?.anomesfacturacion]);

  const [localidades, setLocalidades] = useState([]);
  const [localidad, setLocalidad] = useState('');

  const [conteos, setConteos] = useState([]);
  const [cargandoLista, setCargandoLista] = useState(false);

  const [busquedaProducto, setBusquedaProducto] = useState('');
  const [opcionesProducto, setOpcionesProducto] = useState([]);
  const [productoSel, setProductoSel] = useState(null);
  const [cantidadFisica, setCantidadFisica] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [conteoCerrado, setConteoCerrado] = useState(false);
  const [fechaCierreConteo, setFechaCierreConteo] = useState(null);
  const [usuarioCierreConteo, setUsuarioCierreConteo] = useState(null);
  const [cerrandoConteo, setCerrandoConteo] = useState(false);

  const payloadBase = useMemo(
    () => ({
      tabla_inventario: tablaInventario,
      
    }),
    [selectedCompany?.anomesfacturacion, tablaInventario]
  );

  const statsConteo = useMemo(() => {
    const total = conteos.length;
    const contados = conteos.filter((r) => filaContada(r, localidad)).length;
    const pendientes = total - contados;
    return { total, contados, pendientes };
  }, [conteos, localidad]);

  const cargarLocalidades = useCallback(async () => {
    if (!puedeLeer || !selectedCompany?.db_empresa) return;
    try {
      const response = await fetch(servidor + '/api/productosbackend_obtenerlocalidades', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
        }),
      });
      if (response.ok) {
        const data = await response.json();
        const locs = data.localidades || [];
        setLocalidades(locs);
        setLocalidad((prev) => prev || (locs[0]?.texto || ''));
      } else {
        const err = await response.json();
        showError(err.Error || 'Error al cargar localidades');
      }
    } catch {
      showError('Error de conexión al cargar localidades');
    }
  }, [selectedCompany, usuarioLog, claveLog, servidor, showError, puedeLeer]);

  useEffect(() => {
    cargarLocalidades();
  }, [cargarLocalidades]);

  const listarConteos = useCallback(async () => {
    const tab = tablaInventario;
    if (!puedeLeer || !selectedCompany?.db_empresa || !tab || !localidad) return;
    setCargandoLista(true);
    try {
      const response = await fetch(servidor + '/api/productosbackend_listarconteofisico', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
          ...payloadBase,
          tabla_inventario: tab,
          localidad,
        }),
      });
      if (response.ok) {
        const data = await response.json();
        setConteos(data.conteos || []);
        setConteoCerrado(!!data.conteo_cerrado);
        setFechaCierreConteo(data.fecha_cierre_conteo || null);
        setUsuarioCierreConteo(data.usuario_cierre_conteo || null);
      } else {
        const err = await response.json();
        setConteoCerrado(false);
        setFechaCierreConteo(null);
        setUsuarioCierreConteo(null);
        showError(err.Error || 'Error al cargar conteos');
      }
    } catch {
      setConteoCerrado(false);
      setFechaCierreConteo(null);
      setUsuarioCierreConteo(null);
      showError('Error de conexión al cargar conteos');
    } finally {
      setCargandoLista(false);
    }
  }, [localidad, payloadBase, selectedCompany, usuarioLog, claveLog, servidor, showError, tablaInventario, puedeLeer]);

  useEffect(() => {
    listarConteos();
  }, [listarConteos]);

  const buscarProductosApi = useCallback(
    async (texto) => {
      const tab = tablaInventario;
      if (!selectedCompany?.db_empresa || !tab || !localidad) return [];
      try {
        const response = await fetch(servidor + '/api/productosbackend_buscarproductos', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id_empresa: selectedCompany,
            usuario: usuarioLog || '',
            password: claveLog || '',
            tabla_inventario: tab,
            descripcion: texto,
            local: localidad,
          }),
        });
        if (!response.ok) return [];
        const data = await response.json();
        return data.productos || [];
      } catch {
        return [];
      }
    },
    [selectedCompany, usuarioLog, claveLog, servidor, tablaInventario, localidad]
  );

  useEffect(() => {
    const t = setTimeout(async () => {
      const r = await buscarProductosApi(busquedaProducto.trim());
      setOpcionesProducto(r);
    }, 300);
    return () => clearTimeout(t);
  }, [busquedaProducto, buscarProductosApi]);

  const enriquecerProducto = async (codigo) => {
    const tab = tablaInventario;
    if (!codigo || !tab) return null;
    try {
      const response = await fetch(servidor + '/api/productosbackend_obtenerproducto', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
          tabla_inventario: tab,
          codigo,
          local: localidad,
        }),
      });
      if (!response.ok) return null;
      const data = await response.json();
      return data.producto || null;
    } catch {
      return null;
    }
  };

  const cantidadSistemaDesdeProducto = (p) => {
    if (!p) return null;
    const v = p.inicial;
    if (v === undefined || v === null || v === '') return null;
    const n = parseFloat(v);
    return Number.isNaN(n) ? null : n;
  };

  const guardarLinea = async () => {
    if (!puedeModificar) {
      showError('No tiene permiso para registrar conteos físicos.');
      return;
    }
    if (conteoCerrado) {
      showError('El conteo físico está cerrado para este período y localidad; no puede registrar más líneas.');
      return;
    }
    if (!usuarioLog) {
      showError('Sesión inválida: falta usuario de login');
      return;
    }
    const codigo = (productoSel?.codigo || '').trim();
    if (!codigo) {
      showError('Seleccione o indique un producto');
      return;
    }
    const tab = tablaInventario;
    if (!tab || !localidad) {
      showError('Seleccione localidad y asegúrese de tener período de facturación en la empresa');
      return;
    }
    let prod = productoSel;
    if (!prod?.descripcion) {
      prod = await enriquecerProducto(codigo);
    }
    const cantSis = cantidadSistemaDesdeProducto(prod);
    let cantFis;
    try {
      cantFis = parseFloat(String(cantidadFisica).replace(',', '.'));
    } catch {
      cantFis = NaN;
    }
    if (Number.isNaN(cantFis)) {
      showError('Indique una cantidad física numérica');
      return;
    }

    setGuardando(true);
    try {
      const response = await fetch(servidor + '/api/productosbackend_guardarconteofisico', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
          ...payloadBase,
          tabla_inventario: tab,
          localidad,
          usuario: usuarioLog,
          lineas: [
            {
              codigo,
              descripcion: prod?.descripcion || '',
              cantidad_sistema: cantSis,
              cantidad_fisica: cantFis,
            },
          ],
        }),
      });
      if (response.ok) {
        showSuccess('Conteo registrado');
        setProductoSel(null);
        setBusquedaProducto('');
        setCantidadFisica('');
        await listarConteos();
      } else {
        const err = await response.json();
        showError(err.Error || 'Error al guardar');
      }
    } catch {
      showError('Error de conexión al guardar');
    } finally {
      setGuardando(false);
    }
  };

  const cerrarConteoFisico = async () => {
    if (!puedeCerrar) {
      showError('No tiene permiso para cerrar el conteo físico.');
      return;
    }
    if (conteoCerrado) return;
    if (!usuarioLog) {
      showError('Sesión inválida: falta usuario de login');
      return;
    }
    const tab = tablaInventario;
    if (!tab || !localidad) {
      showError('Seleccione localidad y asegúrese de tener período de facturación en la empresa');
      return;
    }
    if (conteos.length === 0) {
      showError('No hay registros de conteo para cerrar');
      return;
    }
    const msg =
      '¿Cerrar el conteo físico? Se ajustará la existencia del inventario (campo inicial) para que coincida con cada cantidad física registrada, y no podrá agregar más conteos en este período de facturación y localidad.';
    if (!window.confirm(msg)) return;
    setCerrandoConteo(true);
    try {
      const response = await fetch(servidor + '/api/productosbackend_cerrarconteofisico', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
          ...payloadBase,
          tabla_inventario: tab,
          localidad,
          usuario: usuarioLog,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        showSuccess(data.mensaje || 'Conteo físico cerrado');
        await listarConteos();
      } else {
        showError(data.Error || 'Error al cerrar el conteo físico');
      }
    } catch {
      showError('Error de conexión al cerrar el conteo físico');
    } finally {
      setCerrandoConteo(false);
    }
  };

  const cardShellSx = {
    borderRadius: 2.5,
    border: `1px solid ${FP.color.border}`,
    bgcolor: FP.color.cardBg,
    boxShadow: FP.shadow.soft,
    overflow: 'hidden',
  };

  const filterHeaderSx = {
    px: { xs: 2, md: 3 },
    py: 1.6,
    borderBottom: `1px solid ${FP.color.border}`,
    bgcolor: '#FAFBFE',
    display: 'flex',
    alignItems: 'center',
    gap: 1.2,
  };

  const cellHeadSx = {
    fontWeight: 700,
    fontSize: '0.75rem',
    color: FP.color.textSecondary,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    display: 'block',
    mb: 0.5,
  };

  const conteosAnchosColumnas = ['9%', '20%', '12%', '9%', '9%', '10%', '13%', '18%'];
  const conteosColgroup = (
    <colgroup>
      {conteosAnchosColumnas.map((w, i) => (
        <col key={i} style={{ width: w }} />
      ))}
    </colgroup>
  );
  const conteosTableSx = {
    tableLayout: 'fixed',
    width: '100%',
    minWidth: { xs: 820, sm: 980 },
  };

  if (!puedeLeer) {
    return (
      <Box
        component="main"
        sx={{
          minHeight: '100vh',
          bgcolor: FP.color.surface,
        }}
      >
        <PageHeader
          title="Conteo físico de inventario"
          subtitle={
            nombreNegocio ? `${nombreNegocio} · Por localidad (período de facturación)` : 'Por localidad (período de facturación)'
          }
        />
        <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, display: 'flex', justifyContent: 'center' }}>
          <Card
            elevation={0}
            sx={{
              ...cardShellSx,
              maxWidth: 560,
              width: '100%',
              textAlign: 'center',
              p: { xs: 3, md: 4 },
            }}
          >
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: 1.6,
                mx: 'auto',
                mb: 2.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: FP.color.dangerSoft,
                color: FP.color.danger,
              }}
            >
              <LockOutlinedIcon sx={{ fontSize: 30 }} />
            </Box>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: '1.25rem',
                color: FP.color.textPrimary,
                mb: 1,
                letterSpacing: '-0.01em',
              }}
            >
              Acceso denegado
            </Typography>
            <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.95rem', mb: 1.2 }}>
              No tiene permiso del programa «transaccionesinventario» en roles para el conteo físico de inventario.
            </Typography>
            <Typography sx={{ color: FP.color.textMuted, fontSize: '0.85rem' }}>
              Por favor, contacte al administrador del sistema para solicitar los permisos necesarios.
            </Typography>
          </Card>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      component="main"
      sx={{
        minHeight: '100vh',
        bgcolor: FP.color.surface,
      }}
    >
      <PageHeader
        title="Conteo físico de inventario"
        subtitle={nombreNegocio ? `${nombreNegocio} · Por localidad (período de facturación)` : 'Por localidad (período de facturación)'}
      />

      <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
        <Card elevation={0} sx={{ ...cardShellSx, mb: 3 }}>
          <Box sx={filterHeaderSx}>
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: 1.2,
                bgcolor: FP.color.accentBlueSoft,
                color: FP.color.accentBlue,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <SearchIcon sx={{ fontSize: 18 }} />
            </Box>
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
              Parámetros y filtros
            </Typography>
            <Box sx={{ flex: 1 }} />
            <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.78rem', fontWeight: 600 }}>Localidad</Typography>
          </Box>
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.92rem', lineHeight: 1.5, mb: 2 }}>
              Los conteos se guardan por localidad usando el período de facturación de la empresa. Cada ítem con código queda marcado como contado en el registro.
            </Typography>

            {conteoCerrado && (
              <Alert
                severity="success"
                sx={{
                  mb: 2,
                  borderRadius: 2,
                  border: `1px solid ${FP.color.border}`,
                  bgcolor: FP.color.successSoft,
                  '& .MuiAlert-icon': { color: FP.color.successDark },
                }}
              >
                Conteo físico cerrado para este período y localidad; no se permiten nuevos registros de conteo.
                {fechaCierreConteo && ` ${new Date(fechaCierreConteo).toLocaleString('es-DO')}`}
                {usuarioCierreConteo ? ` — ${usuarioCierreConteo}` : ''}
              </Alert>
            )}

            <Grid container spacing={2.5} alignItems="flex-end">
              <Grid item xs={12} md={6}>
                <FormControl fullWidth sx={fpInputSx}>
                  <InputLabel id="loc-conteo">Localidad</InputLabel>
                  <Select
                    labelId="loc-conteo"
                    label="Localidad"
                    value={localidad}
                    onChange={(e) => setLocalidad(e.target.value)}
                  >
                    {localidades.map((l) => (
                      <MenuItem key={l.texto} value={l.texto}>
                        {l.texto}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25 }}>
                  <Button
                    startIcon={<RefreshIcon />}
                    variant="outlined"
                    onClick={() => listarConteos()}
                    disabled={cargandoLista || !tablaInventario}
                    sx={{
                      height: 46,
                      borderColor: FP.color.border,
                      color: FP.color.textPrimary,
                      bgcolor: '#FFFFFF',
                      borderRadius: 1.6,
                      textTransform: 'none',
                      fontWeight: 700,
                      '&:hover': { bgcolor: FP.color.surfaceAlt, borderColor: FP.color.borderStrong },
                    }}
                  >
                    Actualizar lista
                  </Button>
                  <Button
                    startIcon={<CloseIcon />}
                    variant="outlined"
                    onClick={cerrarConteoFisico}
                    disabled={
                      cerrandoConteo ||
                      conteoCerrado ||
                      conteos.length === 0 ||
                      !usuarioLog ||
                      !tablaInventario ||
                      !puedeCerrar
                    }
                    sx={{
                      height: 46,
                      borderColor: FP.color.danger,
                      color: FP.color.danger,
                      borderRadius: 1.6,
                      textTransform: 'none',
                      fontWeight: 700,
                      '&:hover': { bgcolor: FP.color.dangerSoft, borderColor: FP.color.danger },
                      '&.Mui-disabled': {
                        borderColor: FP.color.border,
                        color: FP.color.textMuted,
                      },
                    }}
                  >
                    Cerrar conteo físico
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card elevation={0} sx={{ ...cardShellSx, mb: 3 }}>
          <Box sx={filterHeaderSx}>
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: 1.2,
                bgcolor: FP.color.accentBlueSoft,
                color: FP.color.accentBlue,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Inventory2Icon sx={{ fontSize: 18 }} />
            </Box>
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
              Registrar conteo
            </Typography>
          </Box>
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Grid container spacing={2.5} alignItems="flex-start">
              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={opcionesProducto}
                  getOptionLabel={(o) => o?.texto || ''}
                  value={productoSel}
                  onChange={(_, v) => setProductoSel(v)}
                  inputValue={busquedaProducto}
                  onInputChange={(_, v) => setBusquedaProducto(v)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Buscar producto (por localidad)"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      sx={fpInputSx}
                    />
                  )}
                  disabled={!localidad || !tablaInventario || conteoCerrado || !usuarioLog || !puedeModificar}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Cantidad física"
                  value={cantidadFisica}
                  onChange={(e) => setCantidadFisica(e.target.value)}
                  type="text"
                  inputMode="decimal"
                  disabled={conteoCerrado || !usuarioLog || !tablaInventario || !puedeModificar}
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={guardarLinea}
                  disabled={guardando || !localidad || !tablaInventario || conteoCerrado || !usuarioLog || !puedeModificar}
                  fullWidth
                  sx={{
                    height: 46,
                    bgcolor: FP.color.navy,
                    color: '#FFFFFF',
                    borderRadius: 1.4,
                    textTransform: 'none',
                    fontWeight: 700,
                    boxShadow: 'none',
                    '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
                  }}
                >
                  Guardar conteo
                </Button>
              </Grid>
            </Grid>
            {conteoCerrado && (
              <Alert
                severity="warning"
                sx={{
                  mt: 2,
                  borderRadius: 2,
                  border: `1px solid ${FP.color.border}`,
                  bgcolor: FP.color.warningSoft,
                }}
              >
                Este período de facturación y localidad tienen el conteo cerrado; no puede modificarse desde aquí.
              </Alert>
            )}
          </CardContent>
        </Card>

        {conteos.length > 0 && (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={4}>
              <StatCard
                label="Líneas de conteo"
                value={String(statsConteo.total)}
                icon={DescriptionIcon}
                accent={FP.color.accentBlue}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <StatCard
                label="Contados"
                value={String(statsConteo.contados)}
                icon={FactCheckIcon}
                accent={FP.color.successDark}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <StatCard
                label="Pendientes"
                value={String(statsConteo.pendientes)}
                icon={HourglassEmptyIcon}
                accent={FP.color.navy}
              />
            </Grid>
          </Grid>
        )}

        <Card elevation={0} sx={cardShellSx}>
          <Box
            sx={{
              px: { xs: 2, md: 3 },
              py: 1.6,
              borderBottom: `1px solid ${FP.color.border}`,
              bgcolor: '#FAFBFE',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              flexWrap: 'wrap',
            }}
          >
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
              Detalle del conteo físico
            </Typography>
            {cargandoLista && (
              <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.82rem', fontWeight: 600 }}>
                Cargando…
              </Typography>
            )}
          </Box>
          <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
            {cargandoLista && conteos.length === 0 ? (
              <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={8}>
                <Typography sx={{ color: FP.color.textSecondary, fontWeight: 600 }}>Cargando listado…</Typography>
              </Box>
            ) : conteos.length > 0 ? (
              <TableContainer
                component={Paper}
                elevation={0}
                sx={{
                  border: `1px solid ${FP.color.border}`,
                  borderRadius: 2,
                  overflow: 'auto',
                }}
              >
                <Table sx={conteosTableSx}>
                  {conteosColgroup}
                  <TableBody>
                    <TableRow
                      sx={{
                        position: 'sticky',
                        top: 0,
                        zIndex: 2,
                        '& td': {
                          border: 'none',
                          borderBottom: `2px solid ${FP.color.border}`,
                          bgcolor: FP.color.surfaceAlt,
                        },
                      }}
                    >
                      <TableCell>
                        <Typography component="span" sx={cellHeadSx}>
                          Código
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography component="span" sx={cellHeadSx}>
                          Descripción
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography component="span" sx={cellHeadSx}>
                          Localidad
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          component="span"
                          sx={{ ...cellHeadSx, textAlign: 'right', width: '100%' }}
                        >
                          Cant. sistema
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          component="span"
                          sx={{ ...cellHeadSx, textAlign: 'right', width: '100%' }}
                        >
                          Cant. física
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          component="span"
                          sx={{ ...cellHeadSx, textAlign: 'center', width: '100%' }}
                        >
                          Estado
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography component="span" sx={cellHeadSx}>
                          Usuario
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography component="span" sx={cellHeadSx}>
                          Fecha
                        </Typography>
                      </TableCell>
                    </TableRow>
                    {conteos.map((row) => (
                      <TableRow key={row.id} hover>
                        <TableCell
                          sx={{
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                          title={row.codigo}
                        >
                          {row.codigo}
                        </TableCell>
                        <TableCell
                          sx={{
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                          title={row.descripcion || '-'}
                        >
                          {row.descripcion || '-'}
                        </TableCell>
                        <TableCell
                          sx={{
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                          title={row.localidad_texto || '—'}
                        >
                          {row.localidad_texto || '—'}
                        </TableCell>
                        <TableCell align="right">
                          {row.cantidad_sistema != null ? row.cantidad_sistema : '—'}
                        </TableCell>
                        <TableCell align="right">{row.cantidad_fisica}</TableCell>
                        <TableCell align="center">{statusChip(row, localidad)}</TableCell>
                        <TableCell
                          sx={{
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                          title={row.usuario || '—'}
                        >
                          {row.usuario || '—'}
                        </TableCell>
                        <TableCell
                          sx={{
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {row.fecha_modificacion
                            ? new Date(row.fecha_modificacion).toLocaleString('es-DO')
                            : '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={8}>
                <DescriptionIcon sx={{ fontSize: 56, color: FP.color.textMuted, mb: 2 }} />
                <Typography sx={{ color: FP.color.textPrimary, fontWeight: 700 }} gutterBottom>
                  No hay datos para mostrar
                </Typography>
                <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.9rem' }} textAlign="center" px={2}>
                  Elija localidad; registre conteos o pulse «Actualizar lista» si ya existen líneas guardadas.
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default ConteoFisicoInventario;
